<template>
  <v-card>
    <v-row class="ma-0 py-3" align="center">
      <v-card-title>Case Duration </v-card-title>
      <v-spacer />
      <generic-search
        v-model="carrier"
        hide-details
        clearable
        data-testid="case-duration-carrier-search"
        class="mx-3"
        :prepend-inner-icon="mdiDomain"
        placeholder="Search Carriers"
        label="Carrier"
        :composable="useCarrierSearch"
        @update:model-value="getReportData"
      />
    </v-row>
    <v-divider />

    <v-card-text>
      <v-col cols="12" align="center">
        <div v-if="chartOptions.series.length" class="highcharts-areaspline">
          <highcharts-graph
            :options="chartOptions"
            style="border-radius: 5px"
            data-testid="case-duration-graph"
          />
        </div>
        <div v-else-if="loaded">
          <v-alert color="error"
            >Unable to load Placement Ratio Reports</v-alert
          >
        </div>
        <v-progress-circular v-else indeterminate />
      </v-col>
    </v-card-text>
  </v-card>
</template>

<script setup>
import GenericSearch from "@/components/shared/GenericSearch.vue";

import { getCaseDurationReport } from "@/api/reports.service";
import { parseErrorMessage } from "@/util/helpers";
import { useSnackbarStore } from "@/stores/snackbar";
import { defineAsyncComponent, ref } from "vue";
import { mdiDomain } from "@mdi/js";
import { useCarrierSearch } from "@/components/shared/search/carrier-search";
const HighchartsGraph = defineAsyncComponent(
  () => import("@/components/shared/HighchartsGraph.vue")
);

const props = defineProps({
  all: Boolean
});

const snackbar = useSnackbarStore();

const loaded = ref(false);
const carrier = ref(null);
const chartOptions = ref({
  chart: {
    styledMode: true,
    type: "areaspline",
    style: {
      fontFamily: "Roboto"
    }
  },
  tooltip: {
    shared: false,
    formatter: function () {
      const date = new Date(this.x);
      const monthYear = date.toLocaleString("default", {
        month: "long",
        year: "numeric",
        timeZone: "America/Los_Angeles"
      });

      // Extract the last word from the series name: "Active" or "Inactive"
      const label = this.series.name.split(" ").at(-2); // e.g., "Active"

      const days = this.y.toLocaleString();
      const count = this.point.options.count?.toLocaleString() || "N/A";

      return `${monthYear} ${days} Days for ${count} ${label} Cases`;
    }
  },
  title: {
    text: "Case Duration by Month",
    style: {
      textTransform: "none"
    }
  },
  xAxis: {
    type: "datetime",
    dateTimeLabelFormats: {
      month: "%b %Y"
    }
  },
  yAxis: {
    allowDecimals: false,
    min: 0,
    title: {
      text: "Days to Close"
    }
  },
  plotOptions: {
    areaspline: {
      marker: {
        enabled: false
      }
    }
  },
  series: [],
  defs: {
    gradient0: {
      tagName: "linearGradient",
      id: "gradient-0",
      x1: 0,
      y1: 0,
      x2: 0,
      y2: 1,
      children: [
        {
          tagName: "stop",
          offset: 0
        },
        {
          tagName: "stop",
          offset: 1
        }
      ]
    },
    gradient1: {
      tagName: "linearGradient",
      id: "gradient-1",
      x1: 0,
      y1: 0,
      x2: 0,
      y2: 1,
      children: [
        {
          tagName: "stop",
          offset: 0
        },
        {
          tagName: "stop",
          offset: 1
        }
      ]
    }
  }
});

async function getReportData() {
  try {
    const params = new URLSearchParams();
    if (carrier.value?.id) params.append("carrier_id", carrier.value.id);
    if (props.all) params.append("view", "all");
    const { active, inactive } = await getCaseDurationReport(params);
    chartOptions.value.series = [
      {
        data: active.data,
        name: active.name
      },
      {
        data: inactive.data,
        name: inactive.name,
        visible: false
      }
    ];
  } catch (e) {
    snackbar.showErrorSnackbar({ message: parseErrorMessage(e) });
  } finally {
    loaded.value = true;
  }
}

getReportData();
</script>
